// WishlistPage.jsx — drop into src/pages/
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../hooks/AuthContext";
import Footer from "./Footer";
import config from "../config";

const { API_BASE, SHOP_UPLOADS_URL } = config;

async function apiFetch(url, token, options = {}) {
  const res = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
}

function formatPrice(val) {
  if (val === null || val === undefined) return null;
  return "R" + Number(val).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/* ── Individual wishlist card ── */
function WishlistCard({ product, onRemove, onAddToCart }) {
  const navigate = useNavigate();

  // ── Option-aware stock ──
  // Same fix as ShopProductCard on the homepage: `shop_products.quantity`
  // is meaningless for products with variants (BTU/KG/M/KW/M2) — each row
  // in shop_product_btu_options carries its own quantity. Sum those instead
  // whenever the product has options, so items don't show "Out of stock"
  // just because the base row's quantity happens to be 0.
  const options = product.options || [];
  const hasOptions = options.length > 0;
  const effectiveQuantity = hasOptions
    ? options.reduce((sum, o) => sum + (Number(o.quantity) || 0), 0)
    : Number(product.quantity) || 0;

  const hasDiscount = product.sale_price && Number(product.sale_price) < Number(product.price);
  const isOutOfStock = effectiveQuantity === 0;
  const isLowStock = effectiveQuantity > 0 && effectiveQuantity <= (product.low_stock_alert || 5);

  const handleCtaClick = () => {
    if (isOutOfStock) return;
    if (hasOptions) {
      // Can't add a specific size/variant without the shopper choosing one —
      // send them to the product page where the size selector lives.
      navigate(`/shop/${product.slug}`);
      return;
    }
    onAddToCart(product);
  };

  return (
    <div className="wl-card">
      <div className="wl-card__media" onClick={() => navigate(`/shop/${product.slug}`)}>
        {product.image1 ? (
          <img
            src={`${SHOP_UPLOADS_URL}/${product.image1}`}
            alt={product.name}
            className="wl-card__img"
            loading="lazy"
          />
        ) : (
          <div className="wl-card__img-fallback" aria-hidden="true">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M3 9l4-4 4 4 4-5 4 5" />
            </svg>
          </div>
        )}
        <div className="wl-card__badges">
          {hasDiscount && (
            <span className="wl-badge wl-badge--sale">−{product.discount_percent}%</span>
          )}
          {isOutOfStock && (
            <span className="wl-badge wl-badge--out">Out of stock</span>
          )}
          {isLowStock && (
            <span className="wl-badge wl-badge--low">Low stock</span>
          )}
        </div>

        <button
          className="wl-card__remove"
          onClick={(e) => { e.stopPropagation(); onRemove(product.id); }}
          aria-label={`Remove ${product.name} from wishlist`}
          title="Remove from wishlist"
        >
          <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M4 4l12 12M16 4L4 16" />
          </svg>
        </button>
      </div>

      <div className="wl-card__body">
        {product.brand && <p className="wl-card__brand">{product.brand}</p>}
        <h3
          className="wl-card__name"
          onClick={() => navigate(`/shop/${product.slug}`)}
        >
          {product.name}
        </h3>

        {(product.energy_rating || product.capacity_kw) && (
          <p className="wl-card__spec">
            {product.energy_rating && <span>Energy: <strong>{product.energy_rating}</strong></span>}
            {product.capacity_kw && <span> · {product.capacity_kw} kW</span>}
          </p>
        )}

        <div className="wl-card__price-row">
          {hasDiscount ? (
            <>
              <span className="wl-card__price wl-card__price--sale">{hasOptions ? "From " : ""}{formatPrice(product.sale_price)}</span>
              <span className="wl-card__price wl-card__price--orig">{formatPrice(product.price)}</span>
            </>
          ) : (
            <span className="wl-card__price">{hasOptions ? "From " : ""}{formatPrice(product.price) ?? "Contact us"}</span>
          )}
        </div>

        <div className="wl-card__actions">
          <button
            className="wl-card__cta"
            disabled={isOutOfStock}
            onClick={handleCtaClick}
            aria-label={hasOptions ? `View options for ${product.name}` : `Add ${product.name} to cart`}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
            </svg>
            {isOutOfStock ? "Out of stock" : hasOptions ? "Select options" : "Add to cart"}
          </button>
          <button
            className="wl-card__view"
            onClick={() => navigate(`/shop/${product.slug}`)}
            aria-label={`View ${product.name}`}
          >
            View
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Loading skeleton ── */
function SkeletonCard() {
  return (
    <div className="wl-skeleton">
      <div className="wl-skeleton__media" />
      <div className="wl-skeleton__body">
        <div className="wl-skeleton__line wl-skeleton__line--short" />
        <div className="wl-skeleton__line" />
        <div className="wl-skeleton__line wl-skeleton__line--med" />
        <div className="wl-skeleton__line wl-skeleton__line--short" />
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════
   MAIN WishlistPage
   Props:
     onAddToCart — fn(product) — adds item to cart
   ════════════════════════════════════════════ */
function WishlistPage({ onAddToCart }) {
  const navigate = useNavigate();
  const { token, isLoggedIn } = useAuthContext();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addedIds, setAddedIds] = useState([]);

  const loadWishlist = useCallback(() => {
    if (!token) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    apiFetch(`${API_BASE}/customer-wishlist.php`, token)
      .then((data) => setProducts(data.wishlist || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (!isLoggedIn) {
      setLoading(false);
      return;
    }
    loadWishlist();
  }, [isLoggedIn, loadWishlist]);

  const handleRemove = useCallback((id) => {
    apiFetch(`${API_BASE}/customer-wishlist.php`, token, {
      method: "DELETE",
      body: JSON.stringify({ product_id: id }),
    })
      .then(() => setProducts((prev) => prev.filter((p) => p.id !== id)))
      .catch((e) => alert("Could not remove item: " + e.message));
  }, [token]);

  const handleClearAll = useCallback(async () => {
    try {
      await Promise.all(
        products.map((p) =>
          apiFetch(`${API_BASE}/customer-wishlist.php`, token, {
            method: "DELETE",
            body: JSON.stringify({ product_id: p.id }),
          })
        )
      );
      setProducts([]);
    } catch (e) {
      alert("Could not clear wishlist: " + e.message);
    }
  }, [products, token]);

  const handleAddToCart = useCallback((product) => {
    onAddToCart?.(product);
    setAddedIds((prev) => [...prev, product.id]);
    setTimeout(() => setAddedIds((prev) => prev.filter((id) => id !== product.id)), 2000);
  }, [onAddToCart]);

  /* ── Not logged in ── */
  if (!isLoggedIn) {
    return (
      <>
        <div className="wl">
          <div className="wl-hero">
            <div className="wl-hero__overlay" />
            <div className="wl-hero__content">
              <nav className="wl-bread" aria-label="Breadcrumb">
                <a href="/" className="wl-bread__link">Home</a>
                <span className="wl-bread__sep" aria-hidden="true">›</span>
                <a href="/shop" className="wl-bread__link">Shop</a>
                <span className="wl-bread__sep" aria-hidden="true">›</span>
                <span className="wl-bread__current">Wishlist</span>
              </nav>
              <h1 className="wl-hero__title">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="rgba(255,255,255,0.9)" stroke="none" aria-hidden="true">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
                My Wishlist
              </h1>
            </div>
          </div>
          <div className="wl-body">
            <div className="wl-body__inner">
              <div className="wl-empty">
                <div className="wl-empty__icon" aria-hidden="true">
                  <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                </div>
                <p className="wl-empty__title">Sign in to see your wishlist</p>
                <p className="wl-empty__sub">
                  Create an account or sign in to save and view your favorite products.
                </p>
                <button className="wl-empty__cta" onClick={() => navigate("/shop")}>
                  Browse products
                </button>
              </div>
            </div>
          </div>
          <style>{`
            @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700;800;900&display=swap');
            .wl *, .wl *::before, .wl *::after { box-sizing: border-box; margin: 0; padding: 0; }
            .wl a { text-decoration: none; }
            .wl { font-family: 'Roboto', sans-serif; background: #f5f7fa; min-height: 100vh; }
            .wl-hero { position: relative; height: 200px; background: linear-gradient(135deg, #011546 0%, #013d97 60%, #0255cc 100%); overflow: hidden; }
            .wl-hero__overlay { position: absolute; inset: 0; background: linear-gradient(to bottom, transparent 40%, rgba(1,15,50,0.3) 100%); }
            .wl-hero__content { position: absolute; inset: 0; display: flex; flex-direction: column; justify-content: flex-end; padding: 28px 40px; max-width: 1280px; margin: 0 auto; left: 0; right: 0; }
            .wl-bread { display: flex; align-items: center; gap: 6px; margin-bottom: 10px; }
            .wl-bread__link { font-size: 12.5px; font-weight: 600; color: rgba(255,255,255,0.8); background: rgba(255,255,255,0.12); padding: 3px 10px; border-radius: 4px; }
            .wl-bread__sep { color: rgba(255,255,255,0.4); font-size: 13px; }
            .wl-bread__current { font-size: 12.5px; color: rgba(255,255,255,0.6); }
            .wl-hero__title { display: flex; align-items: center; gap: 12px; font-size: clamp(22px, 3vw, 34px); font-weight: 900; color: #fff; }
            .wl-body { padding: 36px 0 80px; }
            .wl-body__inner { max-width: 1280px; margin: 0 auto; padding: 0 40px; }
            .wl-empty { display: flex; flex-direction: column; align-items: center; gap: 14px; padding: 80px 24px; text-align: center; }
            .wl-empty__icon { width: 80px; height: 80px; border-radius: 50%; background: #f0f4fa; display: flex; align-items: center; justify-content: center; color: #c8d4e8; margin-bottom: 4px; }
            .wl-empty__title { font-size: 18px; font-weight: 800; color: #0f1c38; }
            .wl-empty__sub { font-size: 14px; color: #8895b0; line-height: 1.6; max-width: 320px; }
            .wl-empty__cta { margin-top: 8px; padding: 12px 28px; background: #013d97; color: #fff; border: none; border-radius: 10px; font-family: 'Roboto', sans-serif; font-size: 14px; font-weight: 700; cursor: pointer; }
            .wl-empty__cta:hover { background: #012f77; }
          `}</style>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <div className="wl">
        {/* ── HERO ── */}
        <div className="wl-hero">
          <div className="wl-hero__overlay" />
          <div className="wl-hero__content">
            <nav className="wl-bread" aria-label="Breadcrumb">
              <a href="/" className="wl-bread__link">Home</a>
              <span className="wl-bread__sep" aria-hidden="true">›</span>
              <a href="/shop" className="wl-bread__link">Shop</a>
              <span className="wl-bread__sep" aria-hidden="true">›</span>
              <span className="wl-bread__current">Wishlist</span>
            </nav>
            <h1 className="wl-hero__title">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="rgba(255,255,255,0.9)" stroke="none" aria-hidden="true">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
              My Wishlist
            </h1>
            {products.length > 0 && (
              <p className="wl-hero__subtitle">
                {products.length} saved item{products.length !== 1 ? "s" : ""}
              </p>
            )}
          </div>
        </div>

        {/* ── BODY ── */}
        <div className="wl-body">
          <div className="wl-body__inner">
            {/* Toolbar */}
            {!loading && products.length > 0 && (
              <div className="wl-toolbar">
                <span className="wl-toolbar__count">
                  {products.length} item{products.length !== 1 ? "s" : ""}
                </span>
                <div className="wl-toolbar__actions">
                  <button
                    className="wl-toolbar__clear"
                    onClick={handleClearAll}
                  >
                    Clear wishlist
                  </button>
                  <button
                    className="wl-toolbar__shop"
                    onClick={() => navigate("/shop")}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                    </svg>
                    Continue shopping
                  </button>
                </div>
              </div>
            )}

            {/* Loading */}
            {loading && (
              <div className="wl-grid">
                {[1, 2, 3, 4].map((n) => <SkeletonCard key={n} />)}
              </div>
            )}

            {/* Error */}
            {!loading && error && (
              <div className="wl-empty">
                <div className="wl-empty__icon" aria-hidden="true">
                  <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </div>
                <p className="wl-empty__title">Could not load wishlist</p>
                <p className="wl-empty__sub">{error}</p>
                <button className="wl-empty__cta" onClick={loadWishlist}>Try again</button>
              </div>
            )}

            {/* Empty */}
            {!loading && !error && products.length === 0 && (
              <div className="wl-empty">
                <div className="wl-empty__icon" aria-hidden="true">
                  <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                </div>
                <p className="wl-empty__title">Your wishlist is empty</p>
                <p className="wl-empty__sub">
                  Save products you love by tapping the heart icon while browsing the shop.
                </p>
                <button
                  className="wl-empty__cta"
                  onClick={() => navigate("/shop")}
                >
                  Browse products
                </button>
              </div>
            )}

            {/* Grid */}
            {!loading && !error && products.length > 0 && (
              <div className="wl-grid">
                {products.map((product) => (
                  <WishlistCard
                    key={product.id}
                    product={{ ...product, _added: addedIds.includes(product.id) }}
                    onRemove={handleRemove}
                    onAddToCart={handleAddToCart}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700;800;900&display=swap');
          .wl *, .wl *::before, .wl *::after { box-sizing: border-box; margin: 0; padding: 0; }
          .wl a { text-decoration: none; }
          .wl { font-family: 'Roboto', sans-serif; background: #f5f7fa; min-height: 100vh; }

          .wl-hero {
            position: relative;
            height: 200px;
            background: linear-gradient(135deg, #011546 0%, #013d97 60%, #0255cc 100%);
            overflow: hidden;
          }
          .wl-hero::before {
            content: '';
            position: absolute;
            inset: 0;
            background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.04'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
          }
          .wl-hero__overlay {
            position: absolute;
            inset: 0;
            background: linear-gradient(to bottom, transparent 40%, rgba(1,15,50,0.3) 100%);
          }
          .wl-hero__content {
            position: absolute;
            inset: 0;
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
            padding: 28px 40px;
            max-width: 1280px;
            margin: 0 auto;
            left: 0; right: 0;
          }
          .wl-bread {
            display: flex;
            align-items: center;
            gap: 6px;
            margin-bottom: 10px;
          }
          .wl-bread__link {
            font-size: 12.5px;
            font-weight: 600;
            color: rgba(255,255,255,0.8);
            background: rgba(255,255,255,0.12);
            padding: 3px 10px;
            border-radius: 4px;
            transition: background 0.2s;
          }
          .wl-bread__link:hover { background: rgba(255,255,255,0.22); }
          .wl-bread__sep { color: rgba(255,255,255,0.4); font-size: 13px; }
          .wl-bread__current { font-size: 12.5px; color: rgba(255,255,255,0.6); }
          .wl-hero__title {
            display: flex;
            align-items: center;
            gap: 12px;
            font-size: clamp(22px, 3vw, 34px);
            font-weight: 900;
            color: #fff;
            letter-spacing: -0.02em;
            line-height: 1.1;
          }
          .wl-hero__subtitle {
            font-size: 14px;
            color: rgba(255,255,255,0.7);
            margin-top: 6px;
          }

          .wl-body {
            padding: 36px 0 80px;
          }
          .wl-body__inner {
            max-width: 1280px;
            margin: 0 auto;
            padding: 0 40px;
          }

          .wl-toolbar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 24px;
            flex-wrap: wrap;
            gap: 12px;
          }
          .wl-toolbar__count {
            font-size: 14px;
            font-weight: 600;
            color: #6b7a99;
          }
          .wl-toolbar__actions {
            display: flex;
            align-items: center;
            gap: 10px;
          }
          .wl-toolbar__clear {
            background: none;
            border: 1.5px solid #e2e8f0;
            border-radius: 8px;
            padding: 8px 16px;
            font-family: 'Roboto', sans-serif;
            font-size: 13px;
            font-weight: 700;
            color: #6b7a99;
            cursor: pointer;
            transition: all 0.15s;
          }
          .wl-toolbar__clear:hover {
            border-color: #fecaca;
            color: #dc2626;
            background: #fef2f2;
          }
          .wl-toolbar__shop {
            display: flex;
            align-items: center;
            gap: 7px;
            background: #013d97;
            color: #fff;
            border: none;
            border-radius: 8px;
            padding: 9px 18px;
            font-family: 'Roboto', sans-serif;
            font-size: 13px;
            font-weight: 700;
            cursor: pointer;
            transition: background 0.15s;
          }
          .wl-toolbar__shop:hover { background: #012f77; }

          .wl-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
            gap: 20px;
          }

          .wl-card {
            background: #fff;
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 2px 8px rgba(1, 21, 70, 0.07);
            display: flex;
            flex-direction: column;
            transition: transform 0.2s, box-shadow 0.2s;
          }
          .wl-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(1, 21, 70, 0.12);
          }

          .wl-card__media {
            position: relative;
            aspect-ratio: 1/1;
            background: #f0f4fa;
            cursor: pointer;
            overflow: hidden;
          }
          .wl-card__img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.4s ease;
          }
          .wl-card:hover .wl-card__img {
            transform: scale(1.04);
          }
          .wl-card__img-fallback {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(135deg, #e8edf6, #d4ddf0);
            color: #a0aec0;
          }

          .wl-card__badges {
            position: absolute;
            top: 10px;
            left: 10px;
            display: flex;
            flex-direction: column;
            gap: 4px;
          }
          .wl-badge {
            display: inline-block;
            padding: 3px 9px;
            border-radius: 4px;
            font-size: 10.5px;
            font-weight: 800;
            letter-spacing: 0.04em;
            text-transform: uppercase;
          }
          .wl-badge--sale { background: #ef4444; color: #fff; }
          .wl-badge--out { background: #6b7280; color: #fff; }
          .wl-badge--low { background: #f59e0b; color: #fff; }

          .wl-card__remove {
            position: absolute;
            top: 10px;
            right: 10px;
            width: 30px;
            height: 30px;
            border-radius: 50%;
            background: rgba(255,255,255,0.92);
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #6b7a99;
            box-shadow: 0 1px 4px rgba(0,0,0,0.1);
            transition: all 0.18s;
          }
          .wl-card__remove:hover {
            background: #fee2e2;
            color: #dc2626;
          }

          .wl-card__body {
            padding: 14px 16px 16px;
            display: flex;
            flex-direction: column;
            gap: 5px;
            flex: 1;
          }
          .wl-card__brand {
            font-size: 10.5px;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 0.06em;
            color: #029add;
          }
          .wl-card__name {
            font-size: 14px;
            font-weight: 700;
            color: #0f1c38;
            line-height: 1.35;
            cursor: pointer;
          }
          .wl-card__name:hover { color: #013d97; }
          .wl-card__spec {
            font-size: 12px;
            color: #6b7280;
          }
          .wl-card__spec strong { color: #013d97; }

          .wl-card__price-row {
            display: flex;
            align-items: baseline;
            gap: 8px;
            margin-top: 4px;
          }
          .wl-card__price {
            font-size: 17px;
            font-weight: 800;
            color: #0f1c38;
          }
          .wl-card__price--sale { color: #013d97; }
          .wl-card__price--orig {
            font-size: 13px;
            font-weight: 500;
            color: #9ca3af;
            text-decoration: line-through;
          }

          .wl-card__actions {
            display: flex;
            gap: 8px;
            margin-top: 8px;
          }
          .wl-card__cta {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 7px;
            padding: 10px 14px;
            background: #013d97;
            color: #fff;
            border: none;
            border-radius: 8px;
            font-family: 'Roboto', sans-serif;
            font-size: 13px;
            font-weight: 700;
            cursor: pointer;
            transition: background 0.18s;
          }
          .wl-card__cta:hover:not(:disabled) { background: #012f77; }
          .wl-card__cta:disabled {
            background: #e5e7eb;
            color: #9ca3af;
            cursor: not-allowed;
          }
          .wl-card__view {
            padding: 10px 14px;
            background: #f0f4fa;
            color: #374151;
            border: 1.5px solid #e2e8f0;
            border-radius: 8px;
            font-family: 'Roboto', sans-serif;
            font-size: 13px;
            font-weight: 700;
            cursor: pointer;
            transition: all 0.18s;
            white-space: nowrap;
          }
          .wl-card__view:hover {
            background: #e4ebf5;
            color: #013d97;
            border-color: #c8d4e8;
          }

          .wl-skeleton {
            background: #fff;
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 2px 8px rgba(1, 21, 70, 0.06);
          }
          .wl-skeleton__media {
            aspect-ratio: 1/1;
            background: linear-gradient(90deg, #f0f4fa 25%, #e4ebf5 50%, #f0f4fa 75%);
            background-size: 200% 100%;
            animation: wl-shimmer 1.4s ease-in-out infinite;
          }
          .wl-skeleton__body {
            padding: 14px 16px 16px;
            display: flex;
            flex-direction: column;
            gap: 10px;
          }
          .wl-skeleton__line {
            height: 12px;
            border-radius: 6px;
            background: linear-gradient(90deg, #f0f4fa 25%, #e4ebf5 50%, #f0f4fa 75%);
            background-size: 200% 100%;
            animation: wl-shimmer 1.4s ease-in-out infinite;
          }
          .wl-skeleton__line--short { width: 40%; }
          .wl-skeleton__line--med { width: 60%; }

          @keyframes wl-shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }

          .wl-empty {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 14px;
            padding: 80px 24px;
            text-align: center;
          }
          .wl-empty__icon {
            width: 80px;
            height: 80px;
            border-radius: 50%;
            background: #f0f4fa;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #c8d4e8;
            margin-bottom: 4px;
          }
          .wl-empty__title {
            font-size: 18px;
            font-weight: 800;
            color: #0f1c38;
          }
          .wl-empty__sub {
            font-size: 14px;
            color: #8895b0;
            line-height: 1.6;
            max-width: 320px;
          }
          .wl-empty__cta {
            margin-top: 8px;
            padding: 12px 28px;
            background: #013d97;
            color: #fff;
            border: none;
            border-radius: 10px;
            font-family: 'Roboto', sans-serif;
            font-size: 14px;
            font-weight: 700;
            cursor: pointer;
            transition: background 0.18s;
          }
          .wl-empty__cta:hover { background: #012f77; }

          @media (max-width: 768px) {
            .wl-hero__content { padding: 20px; }
            .wl-body__inner { padding: 0 16px; }
            .wl-body { padding: 24px 0 60px; }
            .wl-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; }
            .wl-toolbar { flex-direction: column; align-items: flex-start; }
          }
          @media (max-width: 400px) {
            .wl-grid { grid-template-columns: 1fr; }
          }

          .wl *:focus-visible {
            outline: 2px solid #029add;
            outline-offset: 2px;
          }
          @media (prefers-reduced-motion: reduce) {
            .wl *, .wl *::before, .wl *::after {
              animation-duration: 0ms !important;
              transition-duration: 0ms !important;
            }
          }
        `}</style>
      </div>
      <Footer />
    </>
  );
}

export default WishlistPage;